/**
 * Nạp atlas đã bake (dist/assets/atlas.png + atlas.json) lúc chạy.
 *
 * - Nếu bake rồi (production / sau `npm run build`): sprite lấy từ atlas —
 *   nhanh, 1 ảnh duy nhất.
 * - Nếu chưa bake (dev `npm run dev`): fetch thất bại → dùng hàm vẽ charforge
 *   trực tiếp (fallback), game vẫn chạy bình thường.
 *
 * Không bao giờ ném lỗi: thất bại nạp atlas chỉ có nghĩa là dùng fallback.
 */

interface OVuongAtlas {
  x: number;
  y: number;
}

interface DuLieuAtlas {
  oVuong: number;
  banDo: Record<string, OVuongAtlas>;
}

let anh: HTMLImageElement | null = null;
let duLieu: DuLieuAtlas | null = null;
let daNapXong = false;

/** Nạp atlas; trả về true nếu có atlas, false nếu dùng fallback. */
export async function napAtlas(coSo = import.meta.env.BASE_URL): Promise<boolean> {
  if (daNapXong) return anh !== null;
  daNapXong = true;
  try {
    const [resJson, resAnh] = await Promise.all([
      fetch(`${coSo}assets/atlas.json`),
      fetch(`${coSo}assets/atlas.png`),
    ]);
    if (!resJson.ok || !resAnh.ok) return false;
    const json = (await resJson.json()) as DuLieuAtlas;
    if (typeof json.oVuong !== 'number' || typeof json.banDo !== 'object' || !json.banDo) {
      return false;
    }
    const blob = await resAnh.blob();
    const url = URL.createObjectURL(blob);
    try {
      anh = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('không tải được atlas.png'));
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
    duLieu = json;
    return true;
  } catch {
    anh = null;
    duLieu = null;
    return false;
  }
}

/** Atlas có chứa sprite này không? */
export function coVungAtlas(id: string): boolean {
  return anh !== null && duLieu !== null && duLieu.banDo[id] !== undefined;
}

/**
 * Vẽ ô sprite từ atlas lên context (đích s×s). Trả về false nếu không có
 * atlas / không có ID — bên gọi dùng fallback vẽ trực tiếp.
 */
export function veVungAtlas(ctx: CanvasRenderingContext2D, id: string, s: number): boolean {
  if (!anh || !duLieu) return false;
  const o = duLieu.banDo[id];
  if (!o) return false;
  const v = duLieu.oVuong;
  ctx.drawImage(anh, o.x, o.y, v, v, 0, 0, s, s);
  return true;
}

/** Kích thước ô atlas (mặc định 48 khi chưa nạp). */
export function kichThuocOVuong(): number {
  return duLieu?.oVuong ?? 48;
}

/** Reset trạng thái nạp (chỉ dùng cho test). */
export function datLaiAtlas(): void {
  anh = null;
  duLieu = null;
  daNapXong = false;
}
